import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';


/* ============================================================
   ICONS
   Same inline-SVG approach already used throughout Connected Rider
   ============================================================ */

const Icon = ({ name, size = 22 }) => {
  const base = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    width: size,
    height: size,
    'aria-hidden': true
  };

  switch (name) {
    case 'health':
      return (
        <svg {...base}>
          <path d="M3 12h4l2-7 4 14 2-7h6" />
        </svg>
      );

    case 'travel':
      return (
        <svg {...base}>
          <path d="M22 2 11 13" />
          <path d="m22 2-7 20-4-9-9-4Z" />
        </svg>
      );

    case 'shield':
      return (
        <svg {...base}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        </svg>
      );

    case 'folder':
      return (
        <svg {...base}>
          <path d="M3 5h6l2 2h10v12H3Z" />
        </svg>
      );

    case 'syringe':
      return (
        <svg {...base}>
          <path d="m18 2 4 4" />
          <path d="m17 7-8.5 8.5" />
          <path d="m14 4 6 6" />
          <path d="m5 13 6 6" />
          <path d="m7 17-4 4" />
        </svg>
      );

    case 'stethoscope':
      return (
        <svg {...base}>
          <path d="M6 3v5a4 4 0 0 0 8 0V3" />
          <path d="M4 3h4" />
          <path d="M12 3h4" />
          <path d="M10 12v2a5 5 0 0 0 10 0v-1" />
          <circle cx="20" cy="10" r="2" />
        </svg>
      );

    case 'image':
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-5-5L5 21" />
        </svg>
      );

    case 'clipboard':
      return (
        <svg {...base}>
          <rect x="5" y="4" width="14" height="17" rx="2" />
          <path d="M9 4V2h6v2" />
          <path d="M9 9h6" />
          <path d="M9 13h6" />
          <path d="M9 17h4" />
        </svg>
      );

    case 'file':
      return (
        <svg {...base}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <path d="M14 2v6h6" />
          <path d="M8 13h8" />
          <path d="M8 17h6" />
        </svg>
      );

    case 'award':
      return (
        <svg {...base}>
          <circle cx="12" cy="8" r="5" />
          <path d="m8.5 12-1.5 9 5-3 5 3-1.5-9" />
        </svg>
      );

    case 'check':
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12 2.5 2.5L16 9" />
        </svg>
      );

    case 'upload':
      return (
        <svg {...base}>
          <path d="M12 16V4" />
          <path d="m7 9 5-5 5 5" />
          <path d="M5 14v6h14v-6" />
        </svg>
      );

    case 'chevronDown':
      return (
        <svg {...base}>
          <path d="m7 9 5 5 5-5" />
        </svg>
      );

    case 'chevronUp':
      return (
        <svg {...base}>
          <path d="m7 15 5-5 5 5" />
        </svg>
      );

    case 'search':
      return (
        <svg {...base}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-4-4" />
        </svg>
      );

    case 'back':
      return (
        <svg {...base}>
          <path d="m15 18-6-6 6-6" />
        </svg>
      );

    default:
      return null;
  }
};


const DOC_TYPES = [
  {
    val: 'vaccination_record',
    label: 'Vaccination Records',
    icon: 'syringe',
    group: 'health'
  },
  {
    val: 'vet_record',
    label: 'Vet Records',
    icon: 'stethoscope',
    group: 'health'
  },
  {
    val: 'xray',
    label: 'X-rays / Imaging',
    icon: 'image',
    group: 'health'
  },
  {
    val: 'coggins',
    label: 'Coggins',
    icon: 'clipboard',
    group: 'travel'
  },
  {
    val: 'health_certificate',
    label: 'Health Certificate',
    icon: 'file',
    group: 'travel'
  },
  {
    val: 'insurance',
    label: 'Insurance',
    icon: 'shield',
    group: 'ownership'
  },
  {
    val: 'registration',
    label: 'Registration',
    icon: 'award',
    group: 'ownership'
  },
  {
    val: 'other',
    label: 'Other Documents',
    icon: 'folder',
    group: 'other'
  }
];


const GROUPS = [
  {
    id: 'health',
    label: 'Health & Veterinary',
    tab: 'Health & Vet',
    icon: 'health',
    tint: '#f6eef6',
    accent: '#6d286c'
  },
  {
    id: 'travel',
    label: 'Travel & Compliance',
    tab: 'Travel',
    icon: 'travel',
    tint: '#fff7e8',
    accent: '#9b6a18'
  },
  {
    id: 'ownership',
    label: 'Ownership & Protection',
    tab: 'Ownership',
    icon: 'shield',
    tint: '#eff7ed',
    accent: '#557b43'
  },
  {
    id: 'other',
    label: 'Other Documents',
    tab: 'Other',
    icon: 'folder',
    tint: '#fff7e8',
    accent: '#af791c'
  }
];


const CURRENT_TYPES = new Set([
  'coggins',
  'health_certificate',
  'insurance',
  'registration'
]);


const EXPIRING_TYPES = new Set([
  'coggins',
  'health_certificate',
  'vaccination_record',
  'insurance',
  'registration'
]);


const PER_HORSE_BULK_TYPES = new Set([
  'coggins',
  'health_certificate',
  'vaccination_record',
  'insurance',
  'registration'
]);


const fmt = value => {
  if (!value) return '';

  const normalized =
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T12:00:00`
      : value;

  return new Date(normalized).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};


const daysUntil = value => {
  if (!value) return null;

  return Math.ceil(
    (
      new Date(`${value}T12:00:00`) -
      new Date()
    ) / 86400000
  );
};


const fileSize = bytes => {
  if (!bytes) return '';

  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};


const titleFromFile = file =>
  String(file?.name || '')
    .replace(/\.[^/.]+$/, '')
    .trim();


const normalizeMatchText = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();


const horseMatchNames = horse => {
  const possible = [
    horse?.name,
    horse?.registered_name,
    horse?.registeredName,
    horse?.show_name,
    horse?.showName,
    horse?.barn_name,
    horse?.barnName
  ];

  return Array.from(
    new Set(
      possible
        .filter(Boolean)
        .map(normalizeMatchText)
        .filter(Boolean)
    )
  );
};


const autoMatchHorseId = (file, horses) => {
  const fileText =
    ` ${normalizeMatchText(titleFromFile(file))} `;

  const candidates = [];

  for (const horse of horses) {
    const aliases =
      horseMatchNames(horse);

    let bestLength = 0;

    for (const alias of aliases) {
      if (
        alias &&
        fileText.includes(` ${alias} `)
      ) {
        bestLength =
          Math.max(
            bestLength,
            alias.length
          );
      }
    }

    if (bestLength > 0) {
      candidates.push({
        horse_id: horse.id,
        score: bestLength
      });
    }
  }

  if (!candidates.length) {
    return null;
  }

  candidates.sort(
    (a, b) => b.score - a.score
  );

  if (
    candidates.length > 1 &&
    candidates[0].score ===
      candidates[1].score
  ) {
    return null;
  }

  return candidates[0].horse_id;
};


const DocumentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const horseFilter =
    searchParams.get('horse') ||
    searchParams.get('horse_id');

  const initialHorse = horseFilter || 'all';

  const [horses, setHorses] = useState([]);
  const [records, setRecords] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState(initialHorse);

  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState('health');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [expandedRecords, setExpandedRecords] = useState({});

  const [showHorsePicker, setShowHorsePicker] = useState(false);
  const [horseSearch, setHorseSearch] = useState('');

  const [showUpload, setShowUpload] = useState(false);
  const [bulkLauncher, setBulkLauncher] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [bulkAssignments, setBulkAssignments] = useState([]);

  const fileRef = useRef(null);


  const buildUploadForm = (
    horseId = selectedHorse,
    documentType = 'coggins'
  ) => ({
    title: '',
    document_type: documentType,
    horse_id:
      horseId && horseId !== 'all'
        ? horseId
        : '',
    record_date: '',
    expiry_date: '',
    issued_by: '',
    provider_phone: '',
    policy_number: '',
    notes: '',
    enable_expiry_reminder:
      EXPIRING_TYPES.has(documentType),
    expiry_reminder_days: '30',
    make_current:
      CURRENT_TYPES.has(documentType)
  });


  const [uploadForm, setUploadForm] =
    useState(() =>
      buildUploadForm(initialHorse, 'coggins')
    );


  const load = async () => {
    setLoading(true);

    const recordQuery =
      selectedHorse !== 'all'
        ? `?horse_id=${selectedHorse}`
        : '';

    try {
      const horsesRes = await api.get('/horses');
      setHorses(horsesRes.data || []);
    } catch (e) {
      console.error('Horse selector load failed:', e);
      toast.error('Failed to load horses');
    }

    try {
      const recordsRes =
        await api.get(`/documents/records${recordQuery}`);

      setRecords(recordsRes.data || []);
    } catch (e) {
      console.error('Document records load failed:', e);
      setRecords([]);

      toast.error(
        `Horse records failed to load${
          e?.response?.status
            ? ` (${e.response.status})`
            : ''
        }`
      );
    }

    if (selectedHorse === 'all') {
      try {
        const allRes = await api.get('/documents/all');
        setAllSections(allRes.data?.sections || []);
      } catch (e) {
        console.error('Organization documents load failed:', e);
        setAllSections([]);
      }
    } else {
      setAllSections([]);
    }

    setLoading(false);
  };


  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHorse]);


  const horseNames = useMemo(() => {
    const names = {};

    horses.forEach(horse => {
      names[horse.id] = horse.name;
    });

    return names;
  }, [horses]);


  const activeHorseName =
    selectedHorse === 'all'
      ? 'All horses'
      : horseNames[selectedHorse] || 'Horse';


  const remainingHorseCount =
    selectedHorse === 'all'
      ? horses.length
      : Math.max(horses.length - 1, 0);


  const filteredHorseChoices = useMemo(() => {
    const q = horseSearch.trim().toLowerCase();

    if (!q) return horses;

    return horses.filter(horse =>
      String(horse.name || '')
        .toLowerCase()
        .includes(q)
    );
  }, [horses, horseSearch]);


  const groupedRecords = useMemo(() => {
    const grouped = {};

    DOC_TYPES.forEach(type => {
      grouped[type.val] =
        records.filter(
          record =>
            record.document_type === type.val
        );
    });

    return grouped;
  }, [records]);


  const orgDocuments = useMemo(() => {
    const map = new Map();

    allSections.forEach(section => {
      (section.items || []).forEach(item => {
        if (
          item.kind === 'org' &&
          !map.has(item.id)
        ) {
          map.set(item.id, item);
        }
      });
    });

    return Array.from(map.values());
  }, [allSections]);


  const buildBulkAssignments = (
    files,
    documentType
  ) => {
    if (
      files.length <= 1 ||
      !PER_HORSE_BULK_TYPES.has(
        documentType
      )
    ) {
      return [];
    }

    return files.map((file, index) => {
      const automatic =
        autoMatchHorseId(
          file,
          horses
        );

      const fallback =
        automatic ||
        (
          selectedHorse !== 'all'
            ? selectedHorse
            : ''
        );

      return {
        key:
          `${file.name}-${file.size}-${index}`,

        file,

        title:
          titleFromFile(file),

        horse_id:
          fallback,

        match:
          automatic
            ? 'auto'
            : fallback
              ? 'selected'
              : 'none'
      };
    });
  };


  const openUpload = (documentType = 'coggins') => {
    setBulkLauncher(false);
    setUploadFiles([]);
    setBulkAssignments([]);

    if (fileRef.current) {
      fileRef.current.value = '';
    }

    setUploadForm(
      buildUploadForm(
        selectedHorse,
        documentType
      )
    );

    setShowUpload(true);
  };


  const openBulkUpload = () => {
    setBulkLauncher(true);
    setUploadFiles([]);
    setBulkAssignments([]);

    if (fileRef.current) {
      fileRef.current.value = '';
    }

    // Start with Coggins because it demonstrates the
    // horse-matching workflow, but the user can change type.
    setUploadForm(
      buildUploadForm(
        'all',
        'coggins'
      )
    );

    setShowUpload(true);
  };


  const closeUpload = () => {
    setShowUpload(false);
    setBulkLauncher(false);
    setUploadFiles([]);
    setBulkAssignments([]);

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };


  const selectHorse = horseId => {
    setSelectedHorse(horseId);
    setHorseSearch('');
    setShowHorsePicker(false);

    const url =
      horseId === 'all'
        ? '/documents'
        : `/documents?horse=${horseId}`;

    navigate(url, { replace: true });
  };


  const jumpToGroup = groupId => {
    setActiveGroup(groupId);

    document
      .getElementById(`document-group-${groupId}`)
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
  };


  const handleFileSelect = event => {
    const files =
      Array.from(event.target.files || []);

    if (!files.length) return;

    setUploadFiles(files);

    const assignments =
      buildBulkAssignments(
        files,
        uploadForm.document_type
      );

    setBulkAssignments(
      assignments
    );

    if (
      !uploadForm.title &&
      files.length === 1
    ) {
      setUploadForm(prev => ({
        ...prev,
        title:
          titleFromFile(
            files[0]
          )
      }));
    }
  };


  const handleTypeChange = value => {
    setUploadForm(prev => ({
      ...prev,
      document_type: value,
      enable_expiry_reminder:
        EXPIRING_TYPES.has(value),
      make_current:
        CURRENT_TYPES.has(value),
      expiry_date:
        EXPIRING_TYPES.has(value)
          ? prev.expiry_date
          : ''
    }));

    setBulkAssignments(
      buildBulkAssignments(
        uploadFiles,
        value
      )
    );
  };


  const isPerHorseBulk =
    uploadFiles.length > 1 &&
    PER_HORSE_BULK_TYPES.has(
      uploadForm.document_type
    );


  const unmatchedBulkCount =
    isPerHorseBulk
      ? bulkAssignments.filter(
          item => !item.horse_id
        ).length
      : 0;


  const matchedBulkCount =
    isPerHorseBulk
      ? bulkAssignments.length -
        unmatchedBulkCount
      : 0;


  const handleUpload = async () => {
    if (!uploadFiles.length) {
      return toast.error(
        'Choose at least one file'
      );
    }

    if (!uploadForm.document_type) {
      return toast.error(
        'Document type is required'
      );
    }


    // --------------------------------------------------------
    // BULK PER-HORSE MODE
    //
    // 20 Coggins = 20 separate records,
    // each attached to its assigned horse.
    // --------------------------------------------------------

    if (isPerHorseBulk) {
      if (!bulkAssignments.length) {
        return toast.error(
          'No files are ready to upload'
        );
      }

      if (unmatchedBulkCount > 0) {
        return toast.error(
          `Assign a horse to all ${unmatchedBulkCount} unmatched ${
            unmatchedBulkCount === 1
              ? 'file'
              : 'files'
          } first`
        );
      }

      setUploading(true);

      const failed = [];
      let succeeded = 0;

      try {
        for (
          const assignment
          of bulkAssignments
        ) {
          try {
            const fd =
              new FormData();

            Object.entries(
              uploadForm
            ).forEach(
              ([key, value]) => {
                if (
                  value !== '' &&
                  value !== null &&
                  value !== undefined &&
                  key !== 'title' &&
                  key !== 'horse_id'
                ) {
                  fd.append(
                    key,
                    typeof value ===
                    'boolean'
                      ? String(value)
                      : value
                  );
                }
              }
            );

            fd.append(
              'title',
              assignment.title ||
                titleFromFile(
                  assignment.file
                )
            );

            fd.append(
              'horse_id',
              assignment.horse_id
            );

            fd.append(
              'files',
              assignment.file
            );

            await api.post(
              '/documents/records',
              fd,
              {
                headers: {
                  'Content-Type':
                    'multipart/form-data'
                }
              }
            );

            succeeded += 1;

          } catch (error) {
            console.error(
              'Bulk document upload failed:',
              assignment.file?.name,
              error
            );

            failed.push(
              assignment
            );
          }
        }

        await load();

        if (!failed.length) {
          closeUpload();

          toast.success(
            `${succeeded} documents matched and uploaded`
          );

        } else {
          setBulkAssignments(
            failed
          );

          setUploadFiles(
            failed.map(
              item => item.file
            )
          );

          toast.error(
            `${succeeded} uploaded · ${failed.length} failed`
          );
        }

      } finally {
        setUploading(false);
      }

      return;
    }


    // --------------------------------------------------------
    // NORMAL MODE
    // One file or one multi-file study/record.
    // --------------------------------------------------------

    if (!uploadForm.title.trim()) {
      return toast.error(
        'Title is required'
      );
    }

    setUploading(true);

    try {
      const fd = new FormData();

      Object.entries(uploadForm).forEach(
        ([key, value]) => {
          if (
            value !== '' &&
            value !== null &&
            value !== undefined
          ) {
            fd.append(
              key,
              typeof value === 'boolean'
                ? String(value)
                : value
            );
          }
        }
      );

      uploadFiles.forEach(file => {
        fd.append('files', file);
      });

      await api.post(
        '/documents/records',
        fd,
        {
          headers: {
            'Content-Type':
              'multipart/form-data'
          }
        }
      );

      const uploadedCount =
        uploadFiles.length;

      closeUpload();
      await load();

      toast.success(
        uploadedCount > 1
          ? `${uploadedCount} files uploaded`
          : 'Document uploaded'
      );

    } catch (e) {
      console.error(e);

      toast.error(
        e?.response?.data?.message ||
        'Upload failed'
      );

    } finally {
      setUploading(false);
    }
  };


  const deleteRecord = async record => {
    const fileCount = record.files?.length || 0;

    const message =
      fileCount > 1
        ? `Delete "${record.title}" and all ${fileCount} files?`
        : `Delete "${record.title}"?`;

    if (!window.confirm(message)) return;

    try {
      await api.delete(
        `/documents/records/${record.id}`
      );

      setRecords(prev =>
        prev.filter(item => item.id !== record.id)
      );

      toast.success('Deleted');

    } catch {
      toast.error('Failed to delete');
    }
  };


  const recordDate = record =>
    record.record_date ||
    record.created_at;


  const metaForRecord = record => {
    const bits = [];

    if (
      selectedHorse === 'all' &&
      record.horse_id &&
      horseNames[record.horse_id]
    ) {
      bits.push(horseNames[record.horse_id]);
    }

    if (record.issued_by) {
      bits.push(record.issued_by);
    }

    if (record.expiry_date) {
      bits.push(`Expires ${fmt(record.expiry_date)}`);
    }

    return bits.join(' · ');
  };


  const renderExpandedRecord = (
    record,
    isCurrent
  ) => {
    const files = record.files || [];
    const fileExpanded =
      !!expandedRecords[record.id];

    return (
      <div
        key={record.id}
        style={{
          padding: '11px 14px 11px 62px',
          borderTop: '1px solid var(--border-color)',
          background: '#fcfbfa'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap'
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                {record.title}
              </span>

              {isCurrent && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: '#417c32',
                    background: '#e8f5e4',
                    borderRadius: 999,
                    padding: '2px 6px'
                  }}
                >
                  CURRENT
                </span>
              )}
            </div>

            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 3
              }}
            >
              {[fmt(recordDate(record)), metaForRecord(record)]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>

          {files.length === 1 && (
            <a
              href={files[0].file_url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary btn-sm"
            >
              View
            </a>
          )}

          {files.length > 1 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() =>
                setExpandedRecords(prev => ({
                  ...prev,
                  [record.id]: !prev[record.id]
                }))
              }
            >
              {files.length} files
            </button>
          )}

          <button
            aria-label="Delete record"
            onClick={() => deleteRecord(record)}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 18,
              padding: '2px 4px'
            }}
          >
            ×
          </button>
        </div>

        {fileExpanded && files.length > 1 && (
          <div
            style={{
              marginTop: 9,
              borderTop: '1px solid var(--border-color)'
            }}
          >
            {files.map(file => (
              <a
                key={file.id}
                href={file.file_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  color: 'var(--text-primary)',
                  textDecoration: 'none',
                  fontSize: 11,
                  padding: '8px 0',
                  borderBottom:
                    '1px solid var(--border-color)'
                }}
              >
                <Icon name="file" size={15} />

                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {file.file_name}
                </span>

                <span
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: 9
                  }}
                >
                  {fileSize(file.file_size)}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };


  const renderTypeRow = (type, group) => {
    const typeRecords =
      groupedRecords[type.val] || [];

    const expanded =
      !!expandedTypes[type.val];

    const isCurrentType =
      CURRENT_TYPES.has(type.val);

    const current =
      isCurrentType
        ? (
            typeRecords.find(r => r.is_current) ||
            typeRecords[0]
          )
        : null;

    const latest =
      current || typeRecords[0];

    const historyCount =
      current
        ? typeRecords.filter(
            r => r.id !== current.id
          ).length
        : 0;

    let countLabel = '';

    if (typeRecords.length) {
      if (type.val === 'xray') {
        countLabel =
          `${typeRecords.length} ${
            typeRecords.length === 1
              ? 'study'
              : 'studies'
          }`;
      } else {
        countLabel = String(typeRecords.length);
      }
    }

    const summaryParts = [];

    if (latest) {
      const date = fmt(recordDate(latest));

      if (!isCurrentType && date) {
        summaryParts.push(`Latest: ${date}`);
      }

      if (!isCurrentType && latest.title) {
        summaryParts.push(latest.title);
      }
    }

    const summary = summaryParts.join(' · ');

    const detailParts = [];

    if (latest?.expiry_date) {
      detailParts.push(
        `Expires ${fmt(latest.expiry_date)}`
      );
    }

    if (latest?.issued_by) {
      detailParts.push(latest.issued_by);
    }

    if (
      type.val === 'xray' &&
      latest?.files?.length > 1
    ) {
      detailParts.push(
        `${latest.files.length} files`
      );
    }

    return (
      <div key={type.val}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            minHeight: 70,
            borderTop:
              type ===
              DOC_TYPES.filter(t => t.group === group.id)[0]
                ? 'none'
                : '1px solid var(--border-color)'
          }}
        >
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: group.tint,
              color: group.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Icon name={type.icon} size={23} />
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flexWrap: 'wrap'
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700
                }}
              >
                {type.label}
              </span>

              {countLabel && (
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: 11,
                    fontWeight: 600
                  }}
                >
                  · {countLabel}
                </span>
              )}

              {current?.is_current && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 800,
                    color: '#417c32',
                    background: '#e8f5e4',
                    padding: '2px 6px',
                    borderRadius: 999
                  }}
                >
                  CURRENT
                </span>
              )}
            </div>

            {!latest ? (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  marginTop: 4
                }}
              >
                Not uploaded yet
              </div>
            ) : isCurrentType ? (
              <>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginTop: 4
                  }}
                >
                  {latest.title}
                </div>

                {detailParts.length > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--text-muted)',
                      marginTop: 2
                    }}
                  >
                    {detailParts.join(' · ')}
                  </div>
                )}

                {historyCount > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                      marginTop: 3
                    }}
                  >
                    {historyCount} previous{' '}
                    {historyCount === 1
                      ? 'record'
                      : 'records'}
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    marginTop: 4
                  }}
                >
                  {summary}
                </div>

                {detailParts.length > 0 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--text-muted)',
                      marginTop: 2
                    }}
                  >
                    {detailParts.join(' · ')}
                  </div>
                )}
              </>
            )}
          </div>

          <button
            onClick={() => openUpload(type.val)}
            aria-label={`Add ${type.label}`}
            style={{
              minWidth: 56,
              height: 34,
              borderRadius: 10,
              border: '1px solid var(--border-color)',
              background: '#faf9f7',
              fontSize: 12,
              fontWeight: 650,
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            + Add
          </button>

          {typeRecords.length > 0 && (
            <button
              onClick={() =>
                setExpandedTypes(prev => ({
                  ...prev,
                  [type.val]: !prev[type.val]
                }))
              }
              aria-label={
                expanded
                  ? `Collapse ${type.label}`
                  : `Expand ${type.label}`
              }
              style={{
                width: 28,
                height: 34,
                border: 'none',
                background: 'transparent',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Icon
                name={
                  expanded
                    ? 'chevronUp'
                    : 'chevronDown'
                }
                size={18}
              />
            </button>
          )}
        </div>

        {expanded &&
          typeRecords.map(record =>
            renderExpandedRecord(
              record,
              !!record.is_current
            )
          )}
      </div>
    );
  };


  const renderGroup = group => {
    const groupTypes =
      DOC_TYPES.filter(
        type => type.group === group.id
      );

    const groupRecords =
      groupTypes.flatMap(
        type => groupedRecords[type.val] || []
      );

    const populatedTypes =
      groupTypes.filter(
        type =>
          (groupedRecords[type.val] || []).length
      ).length;

    const fileCount =
      groupRecords.reduce(
        (total, record) =>
          total + (record.files?.length || 0),
        0
      );

    const collapsed =
      !!collapsedGroups[group.id];

    return (
      <section
        key={group.id}
        id={`document-group-${group.id}`}
        style={{
          scrollMarginTop: 105,
          marginBottom: 14
        }}
      >
        <div
          className="card"
          style={{
            padding: 0,
            overflow: 'hidden',
            borderRadius: 16
          }}
        >
          <button
            onClick={() =>
              setCollapsedGroups(prev => ({
                ...prev,
                [group.id]: !prev[group.id]
              }))
            }
            style={{
              width: '100%',
              border: 'none',
              background: '#fff',
              padding: '11px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              cursor: 'pointer',
              textAlign: 'left',
              borderBottom:
                collapsed
                  ? 'none'
                  : '1px solid var(--border-color)'
            }}
          >
            <span
              style={{
                color: group.accent,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Icon name={group.icon} size={18} />
            </span>

            <span
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '.025em'
              }}
            >
              {group.label}
            </span>

            <span
              style={{
                background: '#f8f5f0',
                borderRadius: 9,
                padding: '4px 8px',
                fontSize: 10,
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap'
              }}
            >
              {populatedTypes} {populatedTypes === 1 ? 'type' : 'types'}
              {' · '}
              {fileCount} {fileCount === 1 ? 'file' : 'files'}
            </span>

            <span
              style={{
                color: 'var(--text-muted)',
                display: 'flex'
              }}
            >
              <Icon
                name={
                  collapsed
                    ? 'chevronDown'
                    : 'chevronUp'
                }
                size={17}
              />
            </span>
          </button>

          {!collapsed &&
            groupTypes.map(type =>
              renderTypeRow(type, group)
            )}
        </div>
      </section>
    );
  };


  const uploadType =
    DOC_TYPES.find(
      type => type.val === uploadForm.document_type
    );


  return (
    <div
      style={{
        background: '#faf8f5',
        minHeight: '100%'
      }}
    >
      {/* ======================================================
          HEADER — follows approved mockup
          ====================================================== */}

      <div
        style={{
          background: 'var(--cr-primary)',
          borderBottom: '1px solid rgba(245,235,221,0.18)',
          padding: '14px 16px 13px',
          position: 'relative'
        }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={{
            position: 'absolute',
            left: 12,
            top: 19,
            width: 34,
            height: 34,
            border: 'none',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.12)',
            color: '#F5EBDD',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon name="back" size={24} />
        </button>

        <div
          style={{
            textAlign: 'center',
            padding: '0 82px'
          }}
        >
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 20,
              fontWeight: 700,
              lineHeight: 1.15,
              color: '#F5EBDD'
            }}
          >
            Document vault
          </div>

          <div
            style={{
              fontSize: 11,
              color: 'rgba(245,235,221,0.82)',
              marginTop: 4
            }}
          >
            Coggins, records, insurance & more
          </div>
        </div>

        <button
          onClick={() => openUpload()}
          style={{
            position: 'absolute',
            right: 14,
            top: 16,
            height: 36,
            border: 'none',
            borderRadius: 10,
            background: '#F5EBDD',
            color: 'var(--cr-primary)',
            fontSize: 12,
            fontWeight: 700,
            padding: '0 15px',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.12)'
          }}
        >
          + Add
        </button>
      </div>


      {/* ======================================================
          HORSE SELECTOR
          Violet +20 ▼ instead of 20+ horse chips
          ====================================================== */}

      <div
        style={{
          background: '#fff',
          padding: '10px 12px'
        }}
      >
        <button
          onClick={() => setShowHorsePicker(true)}
          style={{
            width: '100%',
            height: 50,
            borderRadius: 14,
            border: '1px solid var(--border-color)',
            background: '#fff',
            padding: '0 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            textAlign: 'left'
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: 15,
              fontWeight: 700
            }}
          >
            {activeHorseName}
          </span>

          {horses.length > 0 && (
            <span
              style={{
                background: '#f3e9ee',
                color: 'var(--cr-primary)',
                fontSize: 11,
                fontWeight: 800,
                padding: '4px 9px',
                borderRadius: 999
              }}
            >
              {selectedHorse === 'all'
                ? `${remainingHorseCount} horses`
                : `+${remainingHorseCount}`}
            </span>
          )}

          <span
            style={{
              color: 'var(--text-secondary)',
              display: 'flex'
            }}
          >
            <Icon name="chevronDown" size={19} />
          </span>
        </button>
      </div>


      {/* ======================================================
          CATEGORY JUMP BAR
          ====================================================== */}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(max-content, 1fr))',
          gap: 0,
          overflowX: 'auto',
          background: '#fff',
          borderBottom: '1px solid var(--border-color)',
          padding: '0 10px'
        }}
      >
        {GROUPS.map(group => {
          const active =
            activeGroup === group.id;

          return (
            <button
              key={group.id}
              onClick={() => jumpToGroup(group.id)}
              style={{
                minWidth: 92,
                height: 48,
                border: 'none',
                borderBottom:
                  active
                    ? '2px solid var(--cr-primary)'
                    : '2px solid transparent',
                background: 'transparent',
                color:
                  active
                    ? 'var(--cr-primary)'
                    : 'var(--text-secondary)',
                fontSize: 11,
                fontWeight: active ? 750 : 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                whiteSpace: 'nowrap'
              }}
            >
              <Icon name={group.icon} size={17} />
              {group.tab}
            </button>
          );
        })}
      </div>


      <div
        className="page-content"
        style={{
          paddingTop: 14,
          paddingBottom: 24
        }}
      >
        {loading && (
          <div className="loading-screen">
            <div className="spinner" />
          </div>
        )}

        {!loading &&
          GROUPS.map(renderGroup)}


        {/* MULTI-FILE ACTION FROM APPROVED MOCKUP */}

        {!loading && (
          <button
            onClick={openBulkUpload}
            style={{
              width: '100%',
              border: '1px solid #eadfd5',
              borderRadius: 16,
              background: '#fbf4ec',
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              textAlign: 'left',
              cursor: 'pointer',
              marginBottom: 16
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'var(--cr-primary)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Icon name="upload" size={19} />
            </span>

            <span style={{ flex: 1 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                Upload multiple files at once
              </span>

              <span
                style={{
                  display: 'block',
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  marginTop: 2
                }}
              >
                Select up to 20 files from photos, camera or files.
              </span>
            </span>

            <span
              style={{
                background: 'var(--cr-primary)',
                color: '#fff',
                borderRadius: 9,
                padding: '8px 11px',
                fontSize: 10,
                fontWeight: 700
              }}
            >
              Upload files
            </span>
          </button>
        )}


        {/* Preserve existing organization docs */}

        {!loading &&
          selectedHorse === 'all' &&
          orgDocuments.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <div
                className="card"
                style={{
                  padding: 0,
                  overflow: 'hidden',
                  borderRadius: 16
                }}
              >
                <div
                  style={{
                    padding: '11px 14px',
                    borderBottom:
                      '1px solid var(--border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9
                  }}
                >
                  <Icon name="folder" size={18} />

                  <div
                    style={{
                      flex: 1,
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase'
                    }}
                  >
                    Barn & Organization
                  </div>

                  <span
                    style={{
                      background: '#f8f5f0',
                      borderRadius: 9,
                      padding: '4px 8px',
                      fontSize: 10
                    }}
                  >
                    {orgDocuments.length}
                  </span>
                </div>

                {orgDocuments.map(item => (
                  <button
                    key={item.id}
                    onClick={() =>
                      item.org_id &&
                      navigate(
                        `/organizations/${item.org_id}/documents`
                      )
                    }
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '11px 14px',
                      border: 'none',
                      borderBottom:
                        '1px solid var(--border-color)',
                      background: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <span
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: '#f5f2ef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icon name="file" size={20} />
                    </span>

                    <span style={{ flex: 1 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700
                        }}
                      >
                        {item.title}
                      </span>

                      <span
                        style={{
                          display: 'block',
                          fontSize: 10,
                          color: 'var(--text-muted)',
                          marginTop: 2
                        }}
                      >
                        {item.org_name}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
      </div>


      {/* ======================================================
          HORSE PICKER — MOBILE-FIRST BOTTOM SHEET
          ====================================================== */}

      {showHorsePicker && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setShowHorsePicker(false);
            }
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: '78vh',
              overflowY: 'auto',
              margin: '0 auto',
              background: '#fff',
              borderRadius: '22px 22px 0 0',
              padding: '10px 16px 24px'
            }}
          >
            <div
              style={{
                width: 42,
                height: 4,
                borderRadius: 999,
                background: '#d8d4d0',
                margin: '0 auto 15px'
              }}
            />

            <div
              style={{
                fontSize: 17,
                fontWeight: 750,
                marginBottom: 12
              }}
            >
              Choose a horse
            </div>

            <div
              style={{
                height: 42,
                borderRadius: 12,
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 12px',
                marginBottom: 10
              }}
            >
              <span style={{ color: 'var(--text-muted)' }}>
                <Icon name="search" size={18} />
              </span>

              <input
                value={horseSearch}
                onChange={event =>
                  setHorseSearch(event.target.value)
                }
                autoFocus
                placeholder="Search horses"
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: 13,
                  background: 'transparent'
                }}
              />
            </div>

            <button
              onClick={() => selectHorse('all')}
              style={{
                width: '100%',
                height: 48,
                border: 'none',
                borderBottom:
                  '1px solid var(--border-color)',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight:
                  selectedHorse === 'all' ? 750 : 600
              }}
            >
              <span style={{ flex: 1 }}>
                All horses
              </span>

              <span
                style={{
                  fontSize: 10,
                  color: 'var(--text-muted)'
                }}
              >
                {horses.length}
              </span>

              {selectedHorse === 'all' && (
                <span
                  style={{
                    color: '#4f8b3d',
                    marginLeft: 10,
                    display: 'flex'
                  }}
                >
                  <Icon name="check" size={17} />
                </span>
              )}
            </button>

            {filteredHorseChoices.map(horse => (
              <button
                key={horse.id}
                onClick={() => selectHorse(horse.id)}
                style={{
                  width: '100%',
                  height: 48,
                  border: 'none',
                  borderBottom:
                    '1px solid var(--border-color)',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight:
                    selectedHorse === horse.id ? 750 : 600
                }}
              >
                <span style={{ flex: 1 }}>
                  {horse.name}
                </span>

                {selectedHorse === horse.id && (
                  <span
                    style={{
                      color: '#4f8b3d',
                      display: 'flex'
                    }}
                  >
                    <Icon name="check" size={17} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}


      {/* ======================================================
          UPLOAD BOTTOM SHEET
          Existing record/study architecture retained
          ====================================================== */}

      {showUpload && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              closeUpload();
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '22px 22px 0 0',
              width: '100%',
              maxWidth: 520,
              margin: '0 auto',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '10px 18px 24px'
            }}
          >
            <div
              style={{
                width: 42,
                height: 4,
                borderRadius: 999,
                background: '#d8d4d0',
                margin: '0 auto 13px'
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 15
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 750
                  }}
                >
                  {bulkLauncher
                    ? 'Bulk upload documents'
                    : `Add ${uploadType?.label || 'Document'}`}
                </div>

                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    marginTop: 3
                  }}
                >
                  {bulkLauncher && !uploadFiles.length
                    ? 'Choose a document type, then select your files'
                    : isPerHorseBulk
                      ? 'Each file will be matched to an individual horse'
                      : 'One record can contain multiple files'}
                </div>
              </div>

              <button
                onClick={closeUpload}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 23,
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            {bulkLauncher && (
              <div
                style={{
                  background: '#faf8f5',
                  border: '1px solid var(--border-color)',
                  borderRadius: 14,
                  padding: 12,
                  marginBottom: 14
                }}
              >
                <label
                  className="form-label"
                  style={{
                    display: 'block',
                    marginBottom: 6
                  }}
                >
                  What type of documents are these?
                </label>

                <select
                  className="form-input"
                  value={uploadForm.document_type}
                  onChange={event =>
                    handleTypeChange(event.target.value)
                  }
                >
                  {DOC_TYPES.map(type => (
                    <option
                      key={type.val}
                      value={type.val}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>

                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    marginTop: 7,
                    lineHeight: 1.4
                  }}
                >
                  For Coggins, health certificates, vaccinations,
                  insurance and registration, each file will be
                  matched to a horse automatically.
                </div>
              </div>
            )}


            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border:
                  '2px dashed var(--border-strong)',
                borderRadius: 14,
                padding: '18px 14px',
                textAlign: 'center',
                cursor: 'pointer',
                background:
                  uploadFiles.length
                    ? 'var(--cr-green-light)'
                    : 'var(--bg-secondary)',
                marginBottom: 14
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  color: 'var(--cr-primary)'
                }}
              >
                <Icon name="upload" size={26} />
              </span>

              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  marginTop: 5
                }}
              >
                {uploadFiles.length
                  ? `${uploadFiles.length} ${
                      uploadFiles.length === 1
                        ? 'file selected'
                        : 'files selected'
                    }`
                  : 'Choose one or more files'}
              </div>

              <div
                style={{
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  marginTop: 3
                }}
              >
                PDF, Word, JPG, PNG, WEBP or iPhone images · 10MB each
              </div>
            </div>

            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {!isPerHorseBulk && (
              <div className="form-group">
                <label className="form-label">
                  {uploadForm.document_type === 'xray'
                    ? 'Study / record title *'
                    : 'Record title *'}
                </label>

                <input
                  className="form-input"
                  value={uploadForm.title}
                  placeholder={
                    uploadForm.document_type === 'xray'
                      ? 'e.g. Left front foot'
                      : uploadForm.document_type === 'vet_record'
                        ? 'e.g. Lameness exam'
                        : 'Document title'
                  }
                  onChange={event =>
                    setUploadForm(prev => ({
                      ...prev,
                      title: event.target.value
                    }))
                  }
                />
              </div>
            )}

            {!bulkLauncher && (
              <div className="form-group">
                <label className="form-label">
                  Document type *
                </label>

                <select
                  className="form-input"
                  value={uploadForm.document_type}
                  onChange={event =>
                    handleTypeChange(event.target.value)
                  }
                >
                  {DOC_TYPES.map(type => (
                    <option
                      key={type.val}
                      value={type.val}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isPerHorseBulk && (
              <div
                style={{
                  marginBottom: 14
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8
                  }}
                >
                  <div
                    style={{
                      flex: 1
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 750
                      }}
                    >
                      Match documents to horses
                    </div>

                    <div
                      style={{
                        fontSize: 10,
                        color:
                          'var(--text-muted)',
                        marginTop: 2
                      }}
                    >
                      Horse names found in filenames are matched automatically.
                    </div>
                  </div>

                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <span
                      style={{
                        color: '#4f8b3d'
                      }}
                    >
                      {matchedBulkCount} matched
                    </span>

                    {unmatchedBulkCount > 0 && (
                      <span
                        style={{
                          color: '#b4552e'
                        }}
                      >
                        {' · '}
                        {unmatchedBulkCount} need horse
                      </span>
                    )}
                  </div>
                </div>


                <div
                  style={{
                    border:
                      '1px solid var(--border-color)',
                    borderRadius: 14,
                    overflow: 'hidden',
                    background: '#fff'
                  }}
                >
                  {bulkAssignments.map(
                    (
                      assignment,
                      index
                    ) => {
                      const matchedHorse =
                        horses.find(
                          horse =>
                            horse.id ===
                            assignment.horse_id
                        );

                      return (
                        <div
                          key={
                            assignment.key
                          }
                          style={{
                            padding:
                              '10px 11px',
                            borderTop:
                              index
                                ? '1px solid var(--border-color)'
                                : 'none'
                          }}
                        >
                          <div
                            style={{
                              display:
                                'flex',
                              alignItems:
                                'center',
                              gap: 8,
                              marginBottom:
                                7
                            }}
                          >
                            <span
                              style={{
                                color:
                                  'var(--cr-primary)',
                                display:
                                  'flex'
                              }}
                            >
                              <Icon
                                name="file"
                                size={16}
                              />
                            </span>

                            <div
                              style={{
                                flex: 1,
                                minWidth: 0
                              }}
                            >
                              <div
                                style={{
                                  fontSize:
                                    11,
                                  fontWeight:
                                    650,
                                  overflow:
                                    'hidden',
                                  textOverflow:
                                    'ellipsis',
                                  whiteSpace:
                                    'nowrap'
                                }}
                              >
                                {
                                  assignment
                                    .file
                                    .name
                                }
                              </div>
                            </div>

                            {assignment.match ===
                              'auto' &&
                              assignment.horse_id && (
                                <span
                                  style={{
                                    fontSize:
                                      9,
                                    fontWeight:
                                      750,
                                    color:
                                      '#4f8b3d',
                                    background:
                                      '#edf7e9',
                                    padding:
                                      '2px 6px',
                                    borderRadius:
                                      999
                                  }}
                                >
                                  MATCHED
                                </span>
                              )}

                            {!assignment.horse_id && (
                              <span
                                style={{
                                  fontSize:
                                    9,
                                  fontWeight:
                                    750,
                                  color:
                                    '#a95f2d',
                                  background:
                                    '#fff2e8',
                                  padding:
                                    '2px 6px',
                                  borderRadius:
                                    999
                                }}
                              >
                                NEEDS HORSE
                              </span>
                            )}
                          </div>


                          <select
                            className="form-input"
                            value={
                              assignment.horse_id
                            }
                            onChange={event => {
                              const horseId =
                                event
                                  .target
                                  .value;

                              setBulkAssignments(
                                prev =>
                                  prev.map(
                                    item =>
                                      item.key ===
                                      assignment.key
                                        ? {
                                            ...item,
                                            horse_id:
                                              horseId,
                                            match:
                                              horseId
                                                ? 'manual'
                                                : 'none'
                                          }
                                        : item
                                  )
                              );
                            }}
                            style={{
                              fontSize: 12
                            }}
                          >
                            <option value="">
                              Select horse…
                            </option>

                            {horses.map(
                              horse => (
                                <option
                                  key={
                                    horse.id
                                  }
                                  value={
                                    horse.id
                                  }
                                >
                                  {
                                    horse.name
                                  }
                                </option>
                              )
                            )}
                          </select>

                          {matchedHorse &&
                            assignment.match ===
                              'auto' && (
                              <div
                                style={{
                                  fontSize:
                                    9,
                                  color:
                                    'var(--text-muted)',
                                  marginTop:
                                    4
                                }}
                              >
                                Filename matched{' '}
                                <strong>
                                  {
                                    matchedHorse.name
                                  }
                                </strong>
                              </div>
                            )}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}


            {!isPerHorseBulk && (
            <div className="form-group">
              <label className="form-label">
                Horse
              </label>

              <select
                className="form-input"
                value={uploadForm.horse_id}
                onChange={event =>
                  setUploadForm(prev => ({
                    ...prev,
                    horse_id: event.target.value
                  }))
                }
              >
                <option value="">
                  All horses / general
                </option>

                {horses.map(horse => (
                  <option
                    key={horse.id}
                    value={horse.id}
                  >
                    {horse.name}
                  </option>
                ))}
              </select>
            </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10
              }}
            >
              <div className="form-group">
                <label className="form-label">
                  Record date
                </label>

                <input
                  className="form-input"
                  type="date"
                  value={uploadForm.record_date}
                  onChange={event =>
                    setUploadForm(prev => ({
                      ...prev,
                      record_date: event.target.value
                    }))
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Issued by / provider
                </label>

                <input
                  className="form-input"
                  value={uploadForm.issued_by}
                  placeholder="Dr. Rohde"
                  onChange={event =>
                    setUploadForm(prev => ({
                      ...prev,
                      issued_by: event.target.value
                    }))
                  }
                />
              </div>
            </div>

            {EXPIRING_TYPES.has(
              uploadForm.document_type
            ) && (
              <>
                <div className="form-group">
                  <label className="form-label">
                    Expiration / renewal date
                  </label>

                  <input
                    className="form-input"
                    type="date"
                    value={uploadForm.expiry_date}
                    onChange={event =>
                      setUploadForm(prev => ({
                        ...prev,
                        expiry_date: event.target.value
                      }))
                    }
                  />
                </div>

                {CURRENT_TYPES.has(
                  uploadForm.document_type
                ) && (
                  <div
                    style={{
                      background: '#eff7ed',
                      borderRadius: 12,
                      padding: '10px 12px',
                      marginBottom: 13,
                      fontSize: 10,
                      color: '#4d713f',
                      display: 'flex',
                      gap: 7,
                      alignItems: 'center'
                    }}
                  >
                    <Icon name="check" size={16} />

                    This becomes the current record. Previous records remain in history.
                  </div>
                )}

                {uploadForm.expiry_date && (
                  <div
                    className="card"
                    style={{
                      padding: 12,
                      marginBottom: 13,
                      background: 'var(--bg-secondary)'
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        cursor: 'pointer',
                        fontSize: 11,
                        fontWeight: 650
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          !!uploadForm.enable_expiry_reminder
                        }
                        onChange={event =>
                          setUploadForm(prev => ({
                            ...prev,
                            enable_expiry_reminder:
                              event.target.checked
                          }))
                        }
                      />

                      Remind me before this expires
                    </label>

                    {uploadForm.enable_expiry_reminder && (
                      <select
                        className="form-input"
                        style={{ marginTop: 9 }}
                        value={
                          uploadForm.expiry_reminder_days
                        }
                        onChange={event =>
                          setUploadForm(prev => ({
                            ...prev,
                            expiry_reminder_days:
                              event.target.value
                          }))
                        }
                      >
                        <option value="7">7 days before</option>
                        <option value="14">14 days before</option>
                        <option value="30">30 days before</option>
                        <option value="60">60 days before</option>
                        <option value="90">90 days before</option>
                      </select>
                    )}
                  </div>
                )}
              </>
            )}

            {uploadForm.document_type === 'insurance' && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10
                }}
              >
                <div className="form-group">
                  <label className="form-label">
                    Insurance phone
                  </label>

                  <input
                    className="form-input"
                    value={uploadForm.provider_phone}
                    onChange={event =>
                      setUploadForm(prev => ({
                        ...prev,
                        provider_phone:
                          event.target.value
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Policy number
                  </label>

                  <input
                    className="form-input"
                    value={uploadForm.policy_number}
                    onChange={event =>
                      setUploadForm(prev => ({
                        ...prev,
                        policy_number:
                          event.target.value
                      }))
                    }
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">
                Notes
              </label>

              <textarea
                className="form-input"
                rows="3"
                value={uploadForm.notes}
                onChange={event =>
                  setUploadForm(prev => ({
                    ...prev,
                    notes: event.target.value
                  }))
                }
              />
            </div>

            <button
              className="btn btn-primary btn-full"
              disabled={
                uploading ||
                !uploadFiles.length
              }
              onClick={handleUpload}
            >
              {uploading
                ? 'Uploading...'
                : isPerHorseBulk
                  ? unmatchedBulkCount > 0
                    ? `Assign ${unmatchedBulkCount} ${
                        unmatchedBulkCount === 1
                          ? 'file'
                          : 'files'
                      }`
                    : `Upload ${bulkAssignments.length} documents`
                  : uploadFiles.length > 1
                    ? `Save record with ${uploadFiles.length} files`
                    : 'Save document'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


export default DocumentsPage;
